import * as XLSX from 'xlsx';
import { CapitalizeFirstLetter, DateFormatter } from '../utilityFunctions/utilsFunctions';

const sanitizeFilename = (name) => (name || 'Event').replace(/[^a-z0-9]/gi, '_');

export const formatFoodItemsSummary = (foodItems) => {
  const items = foodItems || [];
  if (items.length === 0) return '';
  return items
    .map((f) => `${f.name || 'Item'} x${f.quantity || 0} ($${Number(f.price || 0).toFixed(2)})`)
    .join(', ');
};

const autoSizeSheet = (ws, sheetData, headerCount) => {
  const colWidths = [];
  sheetData.forEach((row) => {
    row.forEach((cell, colIndex) => {
      const cellLength = cell ? cell.toString().length : 0;
      if (!colWidths[colIndex] || colWidths[colIndex] < cellLength) {
        colWidths[colIndex] = cellLength;
      }
    });
  });
  if (headerCount) {
    const factsColIndex = sheetData[0]?.indexOf('Interesting Facts');
    const participantsColIndex = sheetData[0]?.indexOf('Additional Participants');
    const seatsColIndex = sheetData[0]?.indexOf('Seats Booked');
    if (factsColIndex !== -1) colWidths[factsColIndex] = 50;
    if (participantsColIndex !== -1) colWidths[participantsColIndex] = 60;
    if (seatsColIndex !== -1) colWidths[seatsColIndex] = 70;
  }
  ws['!cols'] = colWidths.map((width) => ({ wch: (width || 10) + 2 }));
};

const getFoodFromItem = (item) => {
  const ticket = item?.ticketId || item;
  return {
    summary: formatFoodItemsSummary(ticket?.foodItems),
    total: ticket?.foodTotal ?? item?.foodTotal ?? 0,
  };
};

const getTicketsBookedCount = (item) => {
  const ticket = item?.ticketId;
  if (ticket?.numberOfTicketsBooked != null && ticket.numberOfTicketsBooked !== '') {
    return Number(ticket.numberOfTicketsBooked) || 0;
  }
  const fr = item?.formResponseId;
  if (!fr) return 0;
  if (fr.isFamily) return 2;
  const adults = Number(fr.numberOfAdults) || 0;
  const kids = Number(fr.numberOfKids) || 0;
  return adults + kids;
};

const buildStatisticsSheet = (reportData) => {
  const statistics = reportData.stats || {};
  const { show } = reportData;
  const eventType = show?.eventType;

  const cancellations =
    reportData.results?.filter((item) => item.ticketId?.paymentStatus === 'cancelled')?.length || 0;

  const financialRows = [
    ['Online Transactions', `$${(statistics?.totalOnline ?? 0).toFixed(2)}`],
    ['Cash Transactions', `$${(statistics?.totalCash ?? 0).toFixed(2)}`],
    ['Platform Commission', `$${(statistics?.totalConvinienceFee ?? 0).toFixed(2)}`],
    ['Payout to Event Manager', `$${(statistics?.payOut ?? 0).toFixed(2)}`],
    ['Refunds Issued', `$${(statistics?.totalRefunded ?? 0).toFixed(2)}`],
    ['Ticket Cancellations', cancellations.toString()],
    ['Total Donations', `$${(statistics?.totalDonation ?? 0).toFixed(2)}`],
    ['Donation Conv. Fee', `$${(statistics?.totalDonationConvFee ?? 0).toFixed(2)}`],
    ['Total Revenue', `$${(statistics?.totalAmountPaid ?? 0).toFixed(2)}`],
  ];

  let statsData;
  if (eventType === 'form') {
    statsData = [
      ['Metrics', 'Value'],
      ['Event Name', `${show?.eventName}`],
      ['Event Date', `${DateFormatter(show?.startDate, 'toEventViewDate')}`],
      ['Total Submissions', `${statistics?.totalBookings || '0'}`],
      [
        'Registrations Left',
        `${statistics?.totalSeatsAvailable ?? 0}/${statistics?.totalNumberOfTickets ?? 0}`,
      ],
      [
        'Final Check-ins',
        `${statistics?.verifiedTickets || '0'}/${statistics?.totalBookings || '0'}`,
      ],
      ...financialRows,
    ];
  } else if (eventType === 'booking') {
    statsData = [
      ['Metrics', 'Value'],
      ['Event Name', `${show?.eventName}`],
      ['Event Date', `${DateFormatter(show?.startDate, 'toEventViewDate')}`],
      ['Total Tickets Booked', `${statistics?.totalBookings || '0'}`],
      ['Total Participants', `${statistics?.totalPeople || '0'}`],
      [
        'Seats Available',
        `${statistics?.totalSeatsAvailable ?? 0}/${statistics?.totalNumberOfTickets ?? 0}`,
      ],
      [
        'Final Check-ins',
        `${statistics?.verifiedTickets || '0'}/${statistics?.totalBookings || '0'}`,
      ],
      ...financialRows,
    ];
  } else if (eventType === 'non-seat-booking') {
    const adultsRow =
      (statistics?.totalAdults ?? 0) > 0 ? [['Total Adults', `${statistics.totalAdults}`]] : [];
    const kidsRow =
      (statistics?.totalKids ?? 0) > 0 ? [['Total Kids', `${statistics.totalKids}`]] : [];
    statsData = [
      ['Metrics', 'Value'],
      ['Event Name', `${show?.eventName}`],
      ['Event Date', `${DateFormatter(show?.startDate, 'toEventViewDate')}`],
      ['Total Tickets Booked', `${statistics?.totalBookings || '0'}`],
      ['Total Participants', `${statistics?.totalPeople || '0'}`],
      ...adultsRow,
      ...kidsRow,
      [
        'Final Check-ins',
        `${statistics?.verifiedTickets || '0'}/${statistics?.totalBookings || '0'}`,
      ],
      ...financialRows,
    ];
  } else if (eventType === 'token') {
    statsData = [
      ['Metrics', 'Value'],
      ['Event Name', `${show?.eventName}`],
      ['Event Date', `${DateFormatter(show?.startDate, 'toEventViewDate')}`],
      ['Rows in this export', `${reportData.results?.length || 0}`],
    ];
  } else {
    statsData = [
      ['Metrics', 'Value'],
      ['Event Name', `${show?.eventName}`],
      ['Event Date', `${DateFormatter(show?.startDate, 'toEventViewDate')}`],
      ['Total Tickets Booked', `${statistics?.totalBookings || '0'}`],
      ['Total Participants', `${statistics?.totalPeople || '0'}`],
      ['Total Adults', `${statistics?.totalAdults || '0'}`],
      ['Total Kids', `${statistics?.totalKids || '0'}`],
      [
        'Final Check-ins',
        `${statistics?.verifiedTickets || '0'}/${statistics?.totalBookings || '0'}`,
      ],
      ...financialRows,
    ];
  }

  const statsWs = XLSX.utils.aoa_to_sheet(statsData);
  statsWs.A1.s = { font: { bold: true } };
  statsWs.B1.s = { font: { bold: true } };
  autoSizeSheet(statsWs, statsData);
  return statsWs;
};

const buildAttendeesSheet = (reportData, options = {}) => {
  const { show } = reportData;
  const eventType = show?.eventType;
  const { includeFoodColumns = false } = options;

  const hasSeats = reportData.results?.some(
    (item) => item?.ticketId?.seatsBooked && item?.ticketId?.seatsBooked !== ''
  );
  const hasParticipants = reportData.results?.some(
    (item) => item?.formResponseId?.participants?.length > 0
  );
  const hasWard = reportData.results?.some(
    (item) => item?.formResponseId?.ward && item?.formResponseId?.ward !== ''
  );
  const hasGuests = reportData.results?.some(
    (item) => item?.formResponseId?.numberOfGuests && item?.formResponseId?.numberOfGuests !== ''
  );

  const isNewFormModel = reportData.results?.some((item) =>
    Array.isArray(item?.formResponseId?.responses)
  );
  const dynamicResponseLabels = isNewFormModel
    ? [
        ...new Set(
          reportData.results
            .flatMap((item) => (item?.formResponseId?.responses || []).map((r) => r.label))
            .filter(Boolean)
        ),
      ]
    : [];

  let attendeeHeaders = [];
  let attendeeData = [];

  if (eventType === 'token') {
    attendeeHeaders = ['User Name', 'Date Joined', 'Tokens', 'Status'];
    attendeeData = [attendeeHeaders];
    reportData.results?.forEach((item) => {
      const booked = item?.ticketId?.bookedDate || item?.createdAt;
      let statusLabel = 'Pending';
      if (item.paymentStatus === 'booked') statusLabel = 'Joined';
      else if (item.paymentStatus === 'cancelled') statusLabel = 'Cancelled';
      else if (item.paymentStatus === 'ticket-pending') statusLabel = 'Pending';
      else if (item.paymentStatus === 'waitlisted') statusLabel = 'Waitlisted';
      else if (item.paymentStatus) statusLabel = CapitalizeFirstLetter(item.paymentStatus);
      const hasTok = item.tokensLeft != null && item.tokensLeft !== '';
      attendeeData.push([
        item.userName || item?.ticketId?.userName || '',
        booked ? DateFormatter(booked, 'toLocaleDateString') : '',
        hasTok ? String(item.tokensLeft) : '',
        statusLabel,
      ]);
    });
  } else if (eventType === 'form') {
    attendeeHeaders = [
      'SL No',
      'Booking Id',
      'Name',
      'Email',
      'Phone',
      'Paid Amount',
      'Convenience Fee',
      'Coupon Discount',
      'Donation Amount',
      'Donation Conv. Fee',
      'Payment Mode',
      'No. of Participants',
    ];
    if (includeFoodColumns) attendeeHeaders.push('Food & Beverages', 'Food Total');
    attendeeHeaders.push('Check-in Status', 'Cancellation Status', ...dynamicResponseLabels);

    if (!isNewFormModel) {
      if (hasWard) {
        attendeeHeaders.push(
          'Partner Name',
          'Donation Amount',
          'Ward',
          'Parish Id',
          'Interesting Facts',
          'Ramp Walk'
        );
      }
      if (hasGuests) attendeeHeaders.push('Visiting Parents', 'Kids Under 5');
      if (hasParticipants) {
        attendeeHeaders.push(
          'Attendee Age',
          'Attendee Gender',
          'Additional Participants',
          'Distance',
          'Booking For'
        );
      }
    }

    attendeeData = [attendeeHeaders];
    reportData.results?.forEach((item, index) => {
      const row = [
        (index + 1).toString(),
        item?.ticketId?.bookingId || '',
        item?.ticketId?.userName || item?.formResponseId?.name || '',
        item?.ticketId?.email || item?.formResponseId?.email || 'Not Added',
        item?.ticketId?.phone || item?.formResponseId?.phone || 'Not Added',
        `$${(item?.ticketId?.totalAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.convinienceFee ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.couponDiscount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationConvFee ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(item?.ticketId?.paymentMethod) || '',
        `${getTicketsBookedCount(item)}`,
      ];
      if (includeFoodColumns) {
        const { summary, total } = getFoodFromItem(item);
        row.push(summary, total);
      }
      row.push(
        item?.isVerified ? 'Checked In' : 'Not Checked In',
        item?.ticketId?.paymentStatus === 'booked' ? 'Not Cancelled' : 'Cancelled'
      );

      if (isNewFormModel) {
        dynamicResponseLabels.forEach((lbl) => {
          const match = (item?.formResponseId?.responses || []).find((r) => r.label === lbl);
          row.push(match?.response ?? '');
        });
      } else {
        if (hasWard) {
          row.push(item?.formResponseId?.partnerName || '-');
          row.push(
            item?.formResponseId?.donationAmount != null
              ? `$${Number(item.formResponseId.donationAmount).toFixed(2)}`
              : '$0.00'
          );
          row.push(item?.formResponseId?.ward || '');
          row.push(item?.formResponseId?.parishId || 'Not Added');
          row.push(
            item?.formResponseId?.facts
              ? item.formResponseId.facts.replace(/(.{80})/g, '$1\n')
              : 'Not Added'
          );
          row.push(item?.formResponseId?.isRampWalk || 'Not Added');
        }
        if (hasGuests) {
          row.push(item?.formResponseId?.numberOfGuests || '0');
          row.push(item?.formResponseId?.numberOfKids5 || '0');
        }
        if (hasParticipants) {
          row.push(item?.formResponseId?.age || 'Not Added');
          row.push(item?.formResponseId?.gender || 'Not Added');
          row.push(
            item?.formResponseId?.participants?.length
              ? item.formResponseId.participants
                  .map((p) =>
                    [p?.name, p?.email, p?.age, p?.phone, p?.gender].filter(Boolean).join(' | ')
                  )
                  .join('\n')
              : 'Not Added'
          );
          row.push(item?.formResponseId?.distance || 'Not Added');
          row.push(item?.formResponseId?.anotherName || 'NA');
        }
      }

      attendeeData.push(row);
    });
  } else if (eventType === 'booking') {
    attendeeHeaders = [
      'SL No',
      'Booking Id',
      'Registered User',
      'Registered Email',
      'Registered Phone',
      'Paid Amount',
      'Convenience Fee',
      'Coupon Discount',
      'Donation Amount',
      'Donation Conv. Fee',
      'Payment Mode',
      'No. of Participants',
    ];
    if (includeFoodColumns) attendeeHeaders.push('Food & Beverages', 'Food Total');
    attendeeHeaders.push('Check-in Status', 'Cancellation Status');
    if (hasSeats) attendeeHeaders.push('Seats Booked');
    if (hasParticipants) attendeeHeaders.push('Additional Participants', 'Booked For');

    attendeeData = [attendeeHeaders];
    reportData.results?.forEach((item, index) => {
      const row = [
        (index + 1).toString(),
        item?.ticketId?.bookingId || '',
        item?.userId?.name || item?.ticketId?.userName || '',
        item?.userId?.email || item?.ticketId?.email || '',
        item?.userId?.phone || item?.ticketId?.phone || '',
        `$${(item?.ticketId?.totalAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.convinienceFee ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.couponDiscount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationConvFee ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(item?.ticketId?.paymentMethod) || '',
        `${item?.ticketId?.numberOfTicketsBooked ?? 0}`,
      ];
      if (includeFoodColumns) {
        const { summary, total } = getFoodFromItem(item);
        row.push(summary, total);
      }
      row.push(
        item?.isVerified ? 'Checked In' : 'Not Checked In',
        item?.ticketId?.paymentStatus === 'booked' ? 'Not Cancelled' : 'Cancelled'
      );
      if (hasSeats) row.push(item?.ticketId?.seatsBooked || '-');
      if (hasParticipants) {
        row.push(
          item?.formResponseId?.participants?.length
            ? item.formResponseId.participants
                .filter((p) => p?.name?.trim() || p?.email?.trim() || p?.phone?.length > 4)
                .map((p) =>
                  [
                    p?.name?.trim() || '-',
                    p?.email?.trim() || '-',
                    p?.phone?.length > 4 ? p.phone : '-',
                  ].join(' | ')
                )
                .join(',\n')
            : 'Not Added'
        );
        row.push(
          item?.ticketId?.bookingForSomeone
            ? `${item?.ticketId?.userName} | ${item?.ticketId?.email} | ${item?.ticketId?.phone}`
            : 'Self'
        );
      }
      attendeeData.push(row);
    });
  } else if (eventType === 'non-seat-booking') {
    const hasKids = reportData.results?.some((item) => (item?.ticketId?.numberOfKids ?? 0) > 0);
    attendeeHeaders = [
      'SL No',
      'Booking Id',
      'Name',
      'Email',
      'Phone',
      'Paid Amount',
      'Convenience Fee',
      'Coupon Discount',
      'Donation Amount',
      'Donation Conv. Fee',
      'Payment Mode',
      'Participants',
    ];
    if (hasKids) attendeeHeaders.push('Kids');
    if (includeFoodColumns) attendeeHeaders.push('Food & Beverages', 'Food Total');
    attendeeHeaders.push('Check-in Status', 'Cancellation Status');

    attendeeData = [attendeeHeaders];
    reportData.results?.forEach((item, index) => {
      const row = [
        (index + 1).toString(),
        item?.ticketId?.bookingId || '',
        item?.ticketId?.userName || '',
        item?.ticketId?.email || '',
        item?.ticketId?.phone || '',
        `$${(item?.ticketId?.totalAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.convinienceFee ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.couponDiscount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationConvFee ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(item?.ticketId?.paymentMethod) || '',
        `${item?.ticketId?.numberOfTicketsBooked ?? 0}`,
      ];
      if (hasKids) row.push(`${item?.ticketId?.numberOfKids ?? 0}`);
      if (includeFoodColumns) {
        const { summary, total } = getFoodFromItem(item);
        row.push(summary, total);
      }
      row.push(
        item?.isVerified ? 'Checked In' : 'Not Checked In',
        item?.ticketId?.paymentStatus === 'booked' ? 'Not Cancelled' : 'Cancelled'
      );
      attendeeData.push(row);
    });
  } else {
    attendeeHeaders = [
      'SL No',
      'Booking Id',
      'Registered User',
      'Registered Email',
      'Registered Phone',
      'Paid Amount',
      'Convenience Fee',
      'Coupon Discount',
      'Donation Amount',
      'Donation Conv. Fee',
      'Payment Mode',
      'No. of Participants',
    ];
    if (includeFoodColumns) attendeeHeaders.push('Food & Beverages', 'Food Total');
    attendeeHeaders.push('Check-in Status', 'Cancellation Status');

    attendeeData = [attendeeHeaders];
    reportData.results?.forEach((item, index) => {
      const row = [
        (index + 1).toString(),
        item?.ticketId?.bookingId || '',
        item?.userId?.name || item?.ticketId?.userName || '',
        item?.userId?.email || item?.ticketId?.email || '',
        item?.userId?.phone || item?.ticketId?.phone || '',
        `$${(item?.ticketId?.totalAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.convinienceFee ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.couponDiscount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationAmount ?? 0).toFixed(2)}`,
        `$${(item?.ticketId?.donationConvFee ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(item?.ticketId?.paymentMethod) || '',
        `${item?.ticketId?.numberOfTicketsBooked ?? 0}`,
      ];
      if (includeFoodColumns) {
        const { summary, total } = getFoodFromItem(item);
        row.push(summary, total);
      }
      row.push(
        item?.isVerified ? 'Checked In' : 'Not Checked In',
        item?.ticketId?.paymentStatus === 'booked' ? 'Not Cancelled' : 'Cancelled'
      );
      attendeeData.push(row);
    });
  }

  const attendeeWs = XLSX.utils.aoa_to_sheet(attendeeData);
  attendeeHeaders.forEach((_, index) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
    if (attendeeWs[cellRef]) attendeeWs[cellRef].s = { font: { bold: true } };
  });
  autoSizeSheet(attendeeWs, attendeeData, attendeeHeaders.length);
  return attendeeWs;
};

export const createExcelReport = (reportData, options = {}) => {
  const { show } = reportData;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildStatisticsSheet(reportData), 'Statistics');
  XLSX.utils.book_append_sheet(wb, buildAttendeesSheet(reportData, options), 'Attendees');
  const filename = `Final_Report_For_${sanitizeFilename(show?.eventName)}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
};

export const createExcelAuditReport = (logs, evtName) => {
  const wb = XLSX.utils.book_new();
  const headers = [
    'Timestamp',
    'User Name',
    'Role',
    'Action Taken',
    'Event/Order ID',
    'IP Address',
  ];
  const rows = (logs || []).map((log) => {
    const timestamp = [log.date, log.time].filter(Boolean).join(', ');
    const userName = [log.name, log.email].filter(Boolean).join('\n');
    const role = log.role ? log.role.charAt(0).toUpperCase() + log.role.slice(1) : '';
    const eventNamePart = log.eventName || evtName || '';
    const bookingIdPart = log.bookingId ? `Booking ID: ${log.bookingId}` : '';
    const eventOrderId = [eventNamePart, bookingIdPart].filter(Boolean).join('\n');
    const ipAddress = [log.ipAddress, log.location].filter(Boolean).join('\n');
    return [timestamp, userName, role, log.action || '', eventOrderId, ipAddress];
  });
  const sheetData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  headers.forEach((_, colIndex) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    if (ws[cellRef]) ws[cellRef].s = { font: { bold: true } };
  });
  autoSizeSheet(ws, sheetData);
  XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
  const filename = `Audit_Log_For_${sanitizeFilename(evtName)}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
};

const formatPendingDate = (ticket) => {
  const d = ticket?.createdAt || ticket?.bookedDate;
  if (!d) return '';
  const iso = new Date(d).toISOString();
  return `${DateFormatter(iso, 'toEventViewDate')}, ${DateFormatter(iso, 'toTime')}`;
};

const formatTiers = (ticket) => {
  if (ticket?.tierDetails?.length) {
    return ticket.tierDetails
      .map((t) => `${t.label}: ${t.quantity} ($${(t.price ?? 0).toFixed(2)} each)`)
      .join('\n');
  }
  if (ticket?.booking?.length) {
    return ticket.booking.map((b) => `${b.section}: ${b.count}`).join('\n');
  }
  return '';
};

export const createExcelPendingTicketsReport = (reportData) => {
  const { show } = reportData;
  const wb = XLSX.utils.book_new();
  const logEventType = show?.eventType;
  const results = reportData.results || [];

  const finalizeSheet = (ws, sheetHeaders, sheetData) => {
    sheetHeaders.forEach((_, i) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: i });
      if (ws[ref]) ws[ref].s = { font: { bold: true } };
    });
    autoSizeSheet(ws, sheetData);
  };

  if (logEventType === 'non-seat-booking') {
    const hasTiers = results.some(
      (item) => item?.ticketId?.tierDetails?.length || item?.ticketId?.booking?.length
    );
    const hasKids = results.some((item) => (item?.ticketId?.numberOfKids ?? 0) > 0);
    const headers = ['SL No', 'Booking ID', 'Name', 'Email', 'Phone', 'Amount', 'Payment Method'];
    if (hasTiers) headers.push('Tier Breakdown');
    else headers.push('Participants');
    if (hasKids) headers.push('Kids');
    headers.push('Initiated At', 'Status');

    const sheetData = [headers];
    results.forEach((item, index) => {
      const ticket = item?.ticketId || {};
      const row = [
        (index + 1).toString(),
        ticket?.bookingId || '',
        ticket?.userName || item?.userId?.name || '',
        ticket?.email || item?.userId?.email || '',
        ticket?.phone || item?.userId?.phone || '',
        `$${(ticket?.totalAmount ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(ticket?.paymentMethod) || '',
      ];
      if (hasTiers) row.push(formatTiers(ticket));
      else row.push(`${ticket?.numberOfTicketsBooked ?? 0}`);
      if (hasKids) row.push(`${ticket?.numberOfKids ?? 0}`);
      row.push(formatPendingDate(ticket), 'Pending');
      sheetData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    finalizeSheet(ws, headers, sheetData);
    XLSX.utils.book_append_sheet(wb, ws, 'Pending Tickets');
  } else {
    const responseLabels = [
      ...new Set(
        results
          .flatMap((item) => (item?.formResponseId?.responses || []).map((r) => r.label))
          .filter(Boolean)
      ),
    ];
    const headers = [
      'SL No',
      'Booking ID',
      'Name',
      'Email',
      'Phone',
      'Amount',
      'Payment Method',
      'No. of Participants',
      ...responseLabels,
      'Initiated At',
      'Status',
    ];
    const sheetData = [headers];
    results.forEach((item, index) => {
      const ticket = item?.ticketId || {};
      const fr = item?.formResponseId || {};
      const row = [
        (index + 1).toString(),
        ticket?.bookingId || '',
        ticket?.userName || fr?.name || item?.userId?.name || '',
        ticket?.email || fr?.email || item?.userId?.email || '',
        ticket?.phone || fr?.phone || item?.userId?.phone || '',
        `$${(ticket?.totalAmount ?? 0).toFixed(2)}`,
        CapitalizeFirstLetter(ticket?.paymentMethod) || '',
        `${getTicketsBookedCount(item)}`,
      ];
      responseLabels.forEach((lbl) => {
        const match = (fr?.responses || []).find((r) => r.label === lbl);
        row.push(match?.response ?? '');
      });
      row.push(formatPendingDate(ticket), 'Pending');
      sheetData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    finalizeSheet(ws, headers, sheetData);
    XLSX.utils.book_append_sheet(wb, ws, 'Pending Submissions');
  }

  const filename = `Pending_Tickets_For_${sanitizeFilename(show?.eventName)}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
};

export const collectFormLabels = (rows) => {
  const formLabelSet = new Set();
  rows.forEach((row) => {
    const combined = [
      ...(row.formResponse?.responses || []),
      ...(row.formResponse?.customQuestionResponses || []),
    ];
    combined.forEach((item) => {
      if (item.label) formLabelSet.add(item.label);
    });
  });
  return Array.from(formLabelSet);
};

export const buildFlatOrdersWorksheetData = (rows, formatDateTime) => {
  const formLabels = collectFormLabels(rows);
  const hasSeats = rows.some((row) => row.seatsBooked);

  return rows.map((row, idx) => {
    const foodItemsSummary = formatFoodItemsSummary(row.foodItems);

    const base = {
      'S.No': idx + 1,
      'Booking ID': row.orderId ? `#${row.orderId}` : '',
      Name: row.name || '',
      Email: row.email || '',
      Phone: row.phone || '',
      'Event Name': row.eventName || '',
      Tickets: row.numberOfTickets || 0,
      'Paid Amount': row.totalAmount || 0,
      'Convenience Fee': row.convinienceFee || 0,
      'Coupon Discount': row.couponDiscount || 0,
      'Donation Amount': row.donationAmount || 0,
      'Donation Conv. Fee': row.donationConvFee || 0,
      'Food & Beverages': foodItemsSummary,
      'Food Total': row.foodTotal || 0,
      'Payment Method': row.paymentMethod || '',
      'Check-in Status': row.isVerified ? 'Checked In' : 'Not Checked In',
      'Cancellation Status':
        row.status === 'booked' || row.status === 'waitlisted' ? 'Not Cancelled' : 'Cancelled',
      Status: row.status || '',
      Date: formatDateTime(row.date, row.timezone, row.timezoneCode),
    };

    if (hasSeats) {
      base['Seats Booked'] = row.seatsBooked || '';
    }

    if (formLabels.length > 0) {
      const combined = [
        ...(row.formResponse?.responses || []),
        ...(row.formResponse?.customQuestionResponses || []),
      ];
      const responseMap = {};
      combined.forEach((item) => {
        if (item.label) responseMap[item.label] = item.response ?? '';
      });
      formLabels.forEach((label) => {
        base[label] = responseMap[label] ?? '';
      });
    }

    return base;
  });
};

export const downloadFlatOrdersWorkbook = (worksheetData, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();

  if (worksheetData.length > 0) {
    const headers = Object.keys(worksheetData[0]);
    const colWidths = headers.map((h) => {
      const maxLen = worksheetData.reduce(
        (max, r) => Math.max(max, String(r[h] ?? '').length),
        h.length
      );
      return { wch: maxLen + 2 };
    });
    worksheet['!cols'] = colWidths;
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
  XLSX.writeFile(workbook, filename);
};

export const fetchAndDownloadEventReport = async ({
  eventId,
  authorization,
  baseUrl,
  statsOverlay = {},
  includeFoodColumns = false,
}) => {
  const response = await fetch(`${baseUrl}/shows/bookings-stats-report/${eventId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
  });
  const data = await response.json();
  if (data.code !== 200) {
    throw new Error(data.message || 'Failed to fetch report data');
  }
  const mergedStats = { ...(data.data.stats || {}), ...statsOverlay };
  return createExcelReport({ ...data.data, stats: mergedStats }, { includeFoodColumns });
};

export const fetchAndDownloadExportLogs = async ({
  eventId,
  eventType,
  eventName,
  authorization,
  baseUrl,
}) => {
  if (eventType === 'booking') {
    const auditParams = new URLSearchParams({ eventId, limit: '1000', page: '1' });
    const response = await fetch(`${baseUrl}/auditlogs?${auditParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authorization,
      },
    });
    const data = await response.json();
    if (!data.success || !data.data) {
      throw new Error(data.message || 'Failed to fetch audit logs');
    }
    const logs = data.data.results || [];
    if (logs.length === 0) {
      return { empty: true, type: 'audit' };
    }
    return { filename: createExcelAuditReport(logs, eventName || 'Event'), type: 'audit' };
  }

  const response = await fetch(`${baseUrl}/shows/pending-bookings/${eventId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
  });
  const data = await response.json();
  if (data.code !== 200) {
    throw new Error(data.message || 'Failed to fetch pending tickets');
  }
  const result = data.data?.result;
  if (!result?.results?.length) {
    return { empty: true, type: 'pending' };
  }
  return { filename: createExcelPendingTicketsReport(result), type: 'pending' };
};
